import { useMemo } from 'react';
import * as R from 'remeda';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { DatapointStatistics } from '@rossum/api-client/queues';
import {
  absoluteMaxSizeStyles,
  commonDataGridStyles,
} from '@rossum/components/data-grid/styles';
import {
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { DataGridPro, gridClasses } from '@rossum/ui/x-data-grid-pro';
import { SchemaFieldWithPathNonNullableField } from '../../../../schemas/getFlattenSchemaFields';
import { AutomationAssistantRates } from '../../core/types';
import { getRowId } from '../../core/utils';
import {
  CUSTOM_PRECISION_FIELD_CLASS_NAME,
  OptimizationTableRowModel,
  useOptimizationTableColumns,
} from '../hooks/useOptimizationTableColumns';
import { CustomPrecision } from '../types';
import { DataCard } from './DataCard';

const LoadingCard = () => (
  <Card variant="outlined" sx={{ flexGrow: 1 }}>
    <CardContent>
      <Skeleton variant="text" height={40} width={40} />
      <Skeleton variant="text" height={40} width={80} />
      <Skeleton variant="text" height={20} width={220} />
      <Skeleton variant="text" height={20} width={250} />
    </CardContent>
  </Card>
);

export type OptimizationState = AutomationAssistantRates & {
  datapointStatistics: DatapointStatistics[];
};

type OptimizationAutomationData = {
  isLoading: boolean;
  currentState: OptimizationState | null;
  targetState: OptimizationState | null;
  schemaMap: Record<string, SchemaFieldWithPathNonNullableField>;
  readOnly: boolean;
};

export const OPTIMIZATION_FORM_ID = 'automation-optimization-form';

export const OptimizeAutomationData = ({
  isLoading,
  currentState,
  targetState,
  schemaMap,
  readOnly,
}: OptimizationAutomationData) => {
  const intl = useIntl();

  const {
    formState: { errors },
  } = useFormContext<CustomPrecision>();

  // errors of react-hook-form does not seem to change reference even after it changes, breaking the memoization in useMemos.
  // as a workaround we copy a new object.
  const stringifiedErrors = JSON.stringify(errors);

  const errorsWithFreshReference = useMemo<FieldErrors<CustomPrecision>>(() => {
    try {
      return JSON.parse(stringifiedErrors) as FieldErrors<CustomPrecision>;
    } catch {
      return {};
    }
  }, [stringifiedErrors]);

  const columns = useOptimizationTableColumns({ readOnly });

  const rows = useMemo<OptimizationTableRowModel[]>(() => {
    if (!currentState) return [];
    const targetErrorRateMap = targetState
      ? R.mapToObj(targetState.datapointStatistics, dp => [
          dp.schemaId,
          dp.estimatedErrorRate,
        ])
      : {};

    return currentState.datapointStatistics.flatMap(dp => {
      const targetErrorRate = targetErrorRateMap[dp.schemaId];
      const errorMessage = errorsWithFreshReference[dp.schemaId]?.message;

      const datapointLabel = schemaMap[dp.schemaId]?.field.label;
      return datapointLabel
        ? ({
            schemaId: dp.schemaId,
            estimatedErrorRate: dp.estimatedErrorRate,
            newErrorRate: targetErrorRate ?? undefined,
            errorRateDifference:
              targetErrorRate && dp.estimatedErrorRate
                ? targetErrorRate - dp.estimatedErrorRate
                : undefined,
            errorMessage,
            datapointLabel,
          } as const satisfies OptimizationTableRowModel)
        : [];
    });
  }, [currentState, errorsWithFreshReference, targetState, schemaMap]);

  return (
    <Stack spacing={3} useFlexGap sx={{ flexGrow: 1 }}>
      <Stack direction="row" spacing={3} useFlexGap>
        {isLoading ? (
          <>
            <LoadingCard />
            <LoadingCard />
          </>
        ) : currentState ? (
          <>
            <DataCard
              title={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.optimization.dataCard.precisionTarget.title',
              })}
              description={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.optimization.dataCard.precisionTarget.description',
              })}
              currentValue={currentState.precisionTarget}
              targetValue={
                targetState ? targetState.precisionTarget : undefined
              }
            />
            <DataCard
              title={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.optimization.dataCard.automationRate.title',
              })}
              description={intl.formatMessage({
                id: 'containers.settings.queues.queue.automation.optimization.dataCard.automationRate.description',
              })}
              currentValue={currentState.automationRate}
              targetValue={targetState?.automationRate}
            />
          </>
        ) : null}
      </Stack>
      <Stack sx={{ flexGrow: 1 }}>
        <Stack direction="row">
          <Stack>
            <Typography variant="h5">
              {readOnly
                ? intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.optimization.table.readOnly.title',
                  })
                : intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.optimization.table.title',
                  })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {readOnly
                ? intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.optimization.table.readOnly.description',
                  })
                : intl.formatMessage({
                    id: 'containers.settings.queues.queue.automation.optimization.table.description',
                  })}
            </Typography>
          </Stack>
        </Stack>
        <Stack sx={{ position: 'relative', flexGrow: 1, minHeight: 400 }}>
          <DataGridPro
            loading={isLoading}
            disableColumnMenu
            disableMultipleRowSelection
            disableRowSelectionOnClick
            disableColumnReorder
            columns={columns}
            rows={rows}
            getRowId={getRowId}
            slots={{ footer: () => null }}
            sx={{
              ...commonDataGridStyles,
              ...absoluteMaxSizeStyles,
              [`& .${gridClasses.row}:hover`]: {
                [`& .${CUSTOM_PRECISION_FIELD_CLASS_NAME}`]: {
                  display: 'block',
                },
              },
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
